import React, { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { AppContext } from "../../../../context/AppContext";
import VideoCallRoom from "../../../../components/videocall/VideoCallRoom";
import "../../../../assets/css/patient/patientlayouts/layoutpages/MyAppointment.css";

const MyAppointment = () => {
  const { baseUrl } = useContext(AppContext);
const token = localStorage.getItem("auth_token");
  const authHeaders = {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
  };

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [cancellingIds, setCancellingIds] = useState({});

  // Prescription Modal State
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [prescriptionAppointment, setPrescriptionAppointment] = useState(null);
  const [prescriptionContent, setPrescriptionContent] = useState(null);
  const [prescriptionLoading, setPrescriptionLoading] = useState(false);

  // ── Fetch appointments ──────────────────────────────────────
  const fetchAppointments = useCallback(() => {
    setLoading(true);
    axios
      .get(`${baseUrl}/Patient/Booking/my`, { headers: authHeaders })
      .then((res) => {
        setAppointments(res.data.data || []);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const getFilteredAppointments = () => {
    if (activeFilter === "upcoming") {
      return appointments.filter((apt) => apt.status === "confirmed" || apt.status === "pending");
    }
    if (activeFilter === "completed") {
      return appointments.filter((apt) => apt.status === "completed");
    }
    return appointments;
  };

  const filteredAppointments = getFilteredAppointments();

  const stats = {
    all: appointments.length,
    upcoming: appointments.filter((a) => a.status === "confirmed" || a.status === "pending").length,
    completed: appointments.filter((a) => a.status === "completed").length,
    confirmed: appointments.filter((a) => a.status === "confirmed").length,
    cancelled: appointments.filter((a) => a.status === "cancelled" || a.status === "rejected").length,
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h, 10);
    const period = hour >= 12 ? "PM" : "AM";
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${String(h12).padStart(2, "0")}:${m} ${period}`;
  };

  const getInitials = (name) => {
    if (!name) return "NA";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2);
  };

// Video call modal ke liye state
const [showVideoCall, setShowVideoCall] = useState(false);
const [activeAppointmentId, setActiveAppointmentId] = useState(null);

const handleJoinSession = (appointment) => {
  setActiveAppointmentId(appointment.id);
  setShowVideoCall(true);
};

  // ── Cancel — only the patient can cancel their own appointment ──
  const handleCancel = (appointment) => {
    Swal.fire({
      title: "Cancel Appointment?",
      html: `Are you sure you want to cancel your session with <strong>${
        appointment.therapist?.full_name || "your therapist"
      }</strong>?`,
      icon: "warning",
      input: "textarea",
      inputPlaceholder: "Reason for cancellation (required)",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel",
      cancelButtonText: "No",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      inputValidator: (value) => {
        if (!value) return "Please provide a reason for cancellation";
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setCancellingIds((prev) => ({ ...prev, [appointment.id]: true }));
        axios
          .post(
            `${baseUrl}/Patient/Booking/${appointment.id}/cancel`,
            { reason: result.value },
            { headers: authHeaders }
          )
          .then(() => {
            Swal.fire({ icon: "success", title: "Cancelled", text: "Your appointment has been cancelled.", timer: 2000, showConfirmButton: false });
            fetchAppointments();
          })
          .catch((err) => {
            Swal.fire({ icon: "error", title: "Failed", text: err?.response?.data?.message || "Something went wrong." });
          })
          .finally(() => setCancellingIds((prev) => ({ ...prev, [appointment.id]: false })));
      }
    });
  };

  // ── Prescription (real data, matches what the therapist writes) ──
  const handleOpenPrescription = (appointment) => {
    setPrescriptionAppointment(appointment);
    setShowPrescriptionModal(true);
    setPrescriptionLoading(true);
    setPrescriptionContent(null);

    axios
      .get(`${baseUrl}/Patient/Prescription/list`, { headers: authHeaders })
      .then((res) => {
        const list = res.data.data || [];
        const match = list.find((p) => p.appointment_id === appointment.id);
        setPrescriptionContent(match || null);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setPrescriptionLoading(false));
  };

  const handleClosePrescription = () => {
    setShowPrescriptionModal(false);
    setPrescriptionAppointment(null);
    setPrescriptionContent(null);
  };

  return (
    <div className="appointments-container">
      {/* Header */}
      <div className="appointments-header">
        <h2 className="appointments-title">
          <svg className="title-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
          </svg>
          My Appointments
        </h2>
        <p className="appointments-subtitle">Manage and join your therapy sessions</p>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card confirmed">
          <div className="stat-number">{stats.confirmed}</div>
          <div className="stat-label">Confirmed</div>
        </div>
        <div className="stat-card completed">
          <div className="stat-number">{stats.completed}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card cancelled">
          <div className="stat-number">{stats.cancelled}</div>
          <div className="stat-label">Cancelled</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button className={`filter-tab ${activeFilter === "all" ? "active" : ""}`} onClick={() => setActiveFilter("all")}>
          <svg className="filter-tab-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
          </svg>
          All Appointments
          <span className="filter-count">{stats.all}</span>
        </button>

        <button className={`filter-tab ${activeFilter === "upcoming" ? "active" : ""}`} onClick={() => setActiveFilter("upcoming")}>
          <svg className="filter-tab-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
          </svg>
          Upcoming
          <span className="filter-count">{stats.upcoming}</span>
        </button>

        <button className={`filter-tab ${activeFilter === "completed" ? "active" : ""}`} onClick={() => setActiveFilter("completed")}>
          <svg className="filter-tab-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
          </svg>
          Completed
          <span className="filter-count">{stats.completed}</span>
        </button>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="appointments-table">
          <thead>
            <tr>
              <th>Date &amp; Time</th>
              <th>Therapist</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4">
                  <div className="empty-state">
                    <p>Loading appointments...</p>
                  </div>
                </td>
              </tr>
            ) : filteredAppointments.length === 0 ? (
              <tr>
                <td colSpan="4">
                  <div className="empty-state">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z" />
                    </svg>
                    <h3>No Appointments Found</h3>
                    <p>No appointments match the selected filter</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredAppointments.map((apt) => {
                const isCancelling = !!cancellingIds[apt.id];
                const canCancel = apt.status === "pending" || apt.status === "confirmed";

                return (
                  <tr key={apt.id}>
                    <td>
                      <div>
                        <strong>{formatDate(apt.appointment_date)}</strong>
                        <div style={{ fontSize: "13px", color: "#64748b", marginTop: "2px" }}>
                          {formatTime(apt.appointment_time)}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="therapist-info">
                        <div className="therapist-avatar">{getInitials(apt.therapist?.full_name)}</div>
                        <span className="therapist-name">{apt.therapist?.full_name || "N/A"}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`status ${apt.status}`}>
                        <span className="status-icon"></span>
                        {apt.status.charAt(0).toUpperCase() + apt.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {apt.status === "confirmed" && (
                          <button className="join-btn" onClick={() => handleJoinSession(apt)}>
                            <svg className="join-btn-icon" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                            </svg>
                            Join Session
                          </button>
                        )}

                        {apt.status === "completed" && (
                          <button className="prescription-btn" onClick={() => handleOpenPrescription(apt)}>
                            <svg className="prescription-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                              <rect x="9" y="3" width="6" height="4" rx="2" />
                              <line x1="9" y1="12" x2="15" y2="12" />
                              <line x1="9" y1="16" x2="13" y2="16" />
                            </svg>
                            Prescription
                          </button>
                        )}

                        {apt.status === "pending" && (
                          <button className="join-btn disabled" disabled>
                            <svg className="join-btn-icon" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                            </svg>
                            Awaiting Confirmation
                          </button>
                        )}

                        {/* Only the patient can cancel their own booking */}
                        {canCancel && (
                          <button
                            className="join-btn disabled"
                            style={{ opacity: isCancelling ? 0.6 : 1, cursor: "pointer", background: "#fee2e2", color: "#dc2626" }}
                            onClick={() => handleCancel(apt)}
                            disabled={isCancelling}
                          >
                            {isCancelling ? "Cancelling..." : "Cancel"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ===== Prescription Modal (real content from therapist) ===== */}
      {showPrescriptionModal && prescriptionAppointment && (
        <div className="pat-rx-modal-overlay" onClick={handleClosePrescription}>
          <div className="pat-rx-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pat-rx-modal-header">
              <div className="pat-rx-header-info">
                <div className="pat-rx-header-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                    <rect x="9" y="3" width="6" height="4" rx="2" />
                    <line x1="9" y1="12" x2="15" y2="12" />
                    <line x1="9" y1="16" x2="13" y2="16" />
                  </svg>
                </div>
                <div>
                  <h3>Prescription</h3>
                  <span className="pat-rx-therapist-name">{prescriptionAppointment.therapist?.full_name || "Therapist"}</span>
                </div>
              </div>
              <button className="pat-rx-close-btn" onClick={handleClosePrescription}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="pat-rx-session-info">
              <span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                {formatDate(prescriptionAppointment.appointment_date)} — {formatTime(prescriptionAppointment.appointment_time)}
              </span>
              <span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                {prescriptionAppointment.session_type}
              </span>
            </div>

            <div className="pat-rx-modal-body">
              <h4 className="pat-rx-list-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                  <rect x="9" y="3" width="6" height="4" rx="2" />
                </svg>
                Doctor's Notes
              </h4>

              {prescriptionLoading ? (
                <p style={{ padding: "16px 0", color: "#64748b" }}>Loading...</p>
              ) : !prescriptionContent ? (
                <div className="pat-rx-empty">
                  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                    <rect x="9" y="3" width="6" height="4" rx="2" />
                    <line x1="9" y1="12" x2="15" y2="12" />
                    <line x1="9" y1="16" x2="13" y2="16" />
                  </svg>
                  <p>No prescription available for this session</p>
                </div>
              ) : (
                <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#1e293b" }}>{prescriptionContent.content}</p>
              )}
            </div>

            <div className="pat-rx-modal-footer">
              <button className="pat-rx-close-footer-btn" onClick={handleClosePrescription}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ===== Video Call Modal ===== */}
{showVideoCall && activeAppointmentId && (
  <VideoCallRoom
    appointmentId={activeAppointmentId}
    userType="patient"
    baseUrl={baseUrl}
    authHeaders={authHeaders}
    onClose={() => {
      setShowVideoCall(false);
      setActiveAppointmentId(null);
      fetchAppointments(); // list refresh, status update dikhane ke liye
    }}
  />
)}
    </div>
  );
};

export default MyAppointment;